import { 
  documentId,
  getDocs, 
  getDoc, 
  query, 
  where
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { 
  getArtistsCollection,
  getCollectionDoc,
  getCollectionPiecesCollection,
  getPiecesCollection
} from '../lib/collections';
import { functions } from '../lib/firebase';
import type { 
  Collection, 
  Artist, 
  Piece 
} from '@shared/types/firestore';

interface GalleryData {
  collection: Collection;
  artists: Artist[];
  pieces: Piece[];
  artistPieceMap: Map<string, Piece[]>;
}

interface ViewerGalleryDataRequest {
  galleryId: string;
  collectionId: string;
}

interface ViewerGalleryDataResponse {
  collection: Collection;
  artists: Artist[];
  pieces: Piece[];
}

interface CollectionPieceMembership {
  pieceId: string;
  artistId?: string;
  artistDisplayName?: string;
  order?: number;
  addedAt?: unknown;
}

type RawArtistData = Partial<Artist> & {
  name?: string;
  biography?: string;
  artistBio?: string;
  description?: string;
};

type RawMembershipData = {
  pieceId?: unknown;
  id?: unknown;
  artistId?: unknown;
  artistID?: unknown;
  artist_id?: unknown;
  artistDisplayName?: unknown;
  artist_display_name?: unknown;
  artistName?: unknown;
  order?: unknown;
  sortOrder?: unknown;
  position?: unknown;
  addedAt?: unknown;
  active?: unknown;
};

const FIRESTORE_ARTIST_IN_QUERY_BATCH_SIZE = 30;
const FIRESTORE_PIECE_IN_QUERY_BATCH_SIZE = 10;
const FIRESTORE_BATCH_CONCURRENCY = 8;

class GalleryService {
  /**
   * Load complete gallery data for a collection
   * Matches the Unity client's LoadCollection flow
   */
  async loadGalleryData(galleryId: string, collectionId: string): Promise<GalleryData> {
    if (functions) {
      return this.loadGalleryDataFromFunction(galleryId, collectionId);
    }

    try {
      console.log(`[Gallery] Loading collection ${collectionId} for gallery ${galleryId}`);

      // 1. Load the collection document
      const collectionRef = getCollectionDoc(galleryId, collectionId);
      const collectionDoc = await getDoc(collectionRef);
      
      if (!collectionDoc.exists()) {
        throw new Error('Collection not found');
      }

      const baseCollection = {
        ...collectionDoc.data(),
        collectionId: collectionDoc.id
      } as Collection;

      console.log(`[Gallery] Collection loaded:`, baseCollection);

      // 2. Load collection membership from the V5 subcollection.
      const memberships = await this.loadCollectionMemberships(galleryId, collectionId);
      const pieceIds = this.collectionPieceIds(baseCollection, memberships);

      console.log(`[Gallery] Found ${memberships.length} collection piece memberships`);

      // 3. Load referenced gallery pieces.
      const pieces = await this.loadPieces(galleryId, pieceIds, memberships);

      // 4. Derive artists from membership and piece data, then enrich with artist docs when present.
      const artistIds = this.deriveArtistIds(memberships, pieces);
      const loadedArtists = await this.loadArtists(galleryId, artistIds);
      const artists = this.buildArtists(artistIds, loadedArtists, memberships, pieces);

      // 5. Preserve the existing UI/store contracts.
      const artistPieceMap = this.createArtistPieceMap(artistIds, pieces);
      const collection = this.withDerivedCollectionMembership(baseCollection, memberships, pieces, artists);

      console.log(`[Gallery] Gallery data loaded successfully`);

      return {
        collection,
        artists,
        pieces,
        artistPieceMap
      };

    } catch (error) {
      console.error('[Gallery] Failed to load gallery data:', error);
      throw error;
    }
  }

  private async loadGalleryDataFromFunction(galleryId: string, collectionId: string): Promise<GalleryData> {
    console.log(`[Gallery] Loading server-built collection ${collectionId} for gallery ${galleryId}`);

    const getViewerGalleryData = httpsCallable<ViewerGalleryDataRequest, ViewerGalleryDataResponse>(
      functions,
      'getViewerGalleryData'
    );
    const startedAt = performance.now();
    const result = await getViewerGalleryData({ galleryId, collectionId });
    const { collection, artists, pieces } = result.data;

    const artistIds = artists
      .map(artist => artist.artistId)
      .filter((artistId): artistId is string => Boolean(artistId));

    console.log(`[Gallery] Server-built gallery loaded successfully`, {
      artists: artists.length,
      pieces: pieces.length,
      elapsedMs: Math.round(performance.now() - startedAt),
    });

    return {
      collection,
      artists,
      pieces,
      artistPieceMap: this.createArtistPieceMap(artistIds, pieces),
    };
  }

  /**
   * Load V5 collection membership documents:
   * galleries/{galleryId}/collections/{collectionId}/collectionPieces/{pieceId}
   */
  private async loadCollectionMemberships(galleryId: string, collectionId: string): Promise<CollectionPieceMembership[]> {
    const membershipCollection = getCollectionPiecesCollection(galleryId, collectionId);
    const snapshot = await getDocs(membershipCollection);

    return snapshot.docs
      .map((membershipDoc): CollectionPieceMembership | null => {
        const data = membershipDoc.data() as RawMembershipData;
        if (data.active === false) return null;

        const pieceId = this.firstString(data.pieceId, data.id, membershipDoc.id);
        if (!pieceId) return null;

        return {
          pieceId,
          artistId: this.firstString(data.artistId, data.artistID, data.artist_id),
          artistDisplayName: this.firstString(
            data.artistDisplayName,
            data.artist_display_name,
            data.artistName
          ),
          order: this.firstNumber(data.order, data.sortOrder, data.position),
          addedAt: data.addedAt
        };
      })
      .filter((membership): membership is CollectionPieceMembership => membership !== null)
      .sort((a, b) => this.compareMemberships(a, b));
  }

  /**
   * Load multiple artists by ID
   */
  private async loadArtists(galleryId: string, artistIds: string[]): Promise<Artist[]> {
    if (artistIds.length === 0) return [];

    const artistsCollection = getArtistsCollection(galleryId);
    const artists: Artist[] = [];

    const chunks = this.chunkArray([...new Set(artistIds)], FIRESTORE_ARTIST_IN_QUERY_BATCH_SIZE);
    const snapshots = await this.mapWithConcurrency(chunks, FIRESTORE_BATCH_CONCURRENCY, (chunk) => {
      const artistsQuery = query(
        artistsCollection,
        where(documentId(), 'in', chunk)
      );

      return getDocs(artistsQuery);
    });

    snapshots.forEach((snapshot) => {
      snapshot.docs.forEach(doc => {
        const data = doc.data() as RawArtistData;

        artists.push({
          ...data,
          displayName: data.displayName ?? data.name ?? 'Artist',
          bio: data.bio ?? data.biography ?? data.artistBio ?? data.description ?? '',
          artistId: doc.id
        } as Artist);
      });
    });

    // Sort artists by lastName for consistent display
    return artists.sort((a, b) => {
      const aName = this.getLastName(a.displayName);
      const bName = this.getLastName(b.displayName);
      return aName.localeCompare(bName);
    });
  }

  /**
   * Load multiple pieces by ID
   */
  private async loadPieces(
    galleryId: string,
    pieceIds: string[],
    memberships: CollectionPieceMembership[]
  ): Promise<Piece[]> {
    if (pieceIds.length === 0) return [];

    const piecesCollection = getPiecesCollection(galleryId);
    const pieces: Piece[] = [];
    const membershipByPieceId = new Map(memberships.map(membership => [membership.pieceId, membership]));

    const chunks = this.chunkArray([...new Set(pieceIds)], FIRESTORE_PIECE_IN_QUERY_BATCH_SIZE);
    const snapshots = await this.mapWithConcurrency(chunks, FIRESTORE_BATCH_CONCURRENCY, (chunk) => {
      const piecesQuery = query(
        piecesCollection,
        where(documentId(), 'in', chunk),
        where('active', '==', true)
      );

      return getDocs(piecesQuery);
    });

    snapshots.forEach((snapshot) => {
      snapshot.docs.forEach(doc => {
        const membership = membershipByPieceId.get(doc.id);
        const data = doc.data();

        pieces.push({
          ...data,
          id: doc.id,
          artistID: data.artistID || membership?.artistId || '',
          artist_display_name: data.artist_display_name || membership?.artistDisplayName
        } as Piece);
      });
    });

    return pieces.sort((a, b) => {
      const aMembership = a.id ? membershipByPieceId.get(a.id) : undefined;
      const bMembership = b.id ? membershipByPieceId.get(b.id) : undefined;
      return this.compareMemberships(aMembership, bMembership) || a.title.localeCompare(b.title);
    });
  }

  /**
   * Create mapping of artist ID to their pieces
   */
  private createArtistPieceMap(artistIds: string[], pieces: Piece[]): Map<string, Piece[]> {
    const artistPieceMap = new Map<string, Piece[]>();

    // Initialize map with empty arrays for each artist
    artistIds.forEach(artistId => {
      artistPieceMap.set(artistId, []);
    });

    // Group pieces by artist
    pieces.forEach(piece => {
      const artistId = piece.artistID;
      if (artistPieceMap.has(artistId)) {
        artistPieceMap.get(artistId)!.push(piece);
      }
    });

    // Sort pieces within each artist by title
    artistPieceMap.forEach(pieces => {
      pieces.sort((a, b) => a.title.localeCompare(b.title));
    });

    return artistPieceMap;
  }

  private deriveArtistIds(memberships: CollectionPieceMembership[], pieces: Piece[]): string[] {
    const artistIds = new Set<string>();
    const membershipByPieceId = new Map(memberships.map(membership => [membership.pieceId, membership]));

    pieces.forEach(piece => {
      const membership = piece.id ? membershipByPieceId.get(piece.id) : undefined;
      const artistId = piece.artistID || membership?.artistId;
      if (artistId) {
        artistIds.add(artistId);
      }
    });

    return Array.from(artistIds);
  }

  private buildArtists(
    artistIds: string[],
    loadedArtists: Artist[],
    memberships: CollectionPieceMembership[],
    pieces: Piece[]
  ): Artist[] {
    const loadedArtistMap = new Map(loadedArtists.map(artist => [artist.artistId, artist]));
    const artistPiecesMap = new Map<string, string[]>();
    const displayNameMap = new Map<string, string>();

    memberships.forEach(membership => {
      const piece = pieces.find(piece => piece.id === membership.pieceId);
      const artistId = piece?.artistID || membership.artistId;
      if (!artistId) return;

      if (!artistPiecesMap.has(artistId)) {
        artistPiecesMap.set(artistId, []);
      }
      artistPiecesMap.get(artistId)!.push(membership.pieceId);

      const displayName = piece?.artist_display_name || membership.artistDisplayName;
      if (displayName && !displayNameMap.has(artistId)) {
        displayNameMap.set(artistId, displayName);
      }
    });

    return artistIds
      .map(artistId => {
        const loadedArtist = loadedArtistMap.get(artistId);
        const pieceIds = artistPiecesMap.get(artistId) || [];

        if (loadedArtist) {
          return {
            ...loadedArtist,
            pieces: pieceIds
          } as Artist;
        }

        return {
          artistId,
          displayName: displayNameMap.get(artistId) || 'Artist',
          bio: '',
          birthYear: null,
          deathYear: null,
          imagePath: '',
          nationality: '',
          active: true,
          pieces: pieceIds
        } as Artist;
      })
      .sort((a, b) => {
        const aName = this.getLastName(a.displayName);
        const bName = this.getLastName(b.displayName);
        return aName.localeCompare(bName);
      });
  }

  private withDerivedCollectionMembership(
    collection: Collection,
    memberships: CollectionPieceMembership[],
    pieces: Piece[],
    artists: Artist[]
  ): Collection {
    if (memberships.length === 0) {
      return {
        ...collection,
        artists: collection.artists || {},
        pieces: collection.pieces || {}
      };
    }

    const pieceById = new Map(pieces.map(piece => [piece.id, piece]));
    const artistNameById = new Map(artists.map(artist => [artist.artistId, artist.displayName]));
    const derivedArtists: Collection['artists'] = {};
    const derivedPieces: Collection['pieces'] = {};

    memberships.forEach(membership => {
      const piece = pieceById.get(membership.pieceId);
      if (!piece?.id) return;

      const artistId = piece.artistID || membership.artistId;
      if (artistId) {
        const existingArtist = derivedArtists[artistId];
        const existingPieces = Array.isArray(existingArtist)
          ? existingArtist
          : existingArtist?.pieces || [];

        derivedArtists[artistId] = {
          name: artistNameById.get(artistId) || piece.artist_display_name || membership.artistDisplayName || 'Artist',
          pieces: [...existingPieces, piece.id]
        };
      }

      derivedPieces[piece.id] = {
        order: membership.order ?? Object.keys(derivedPieces).length,
        addedAt: membership.addedAt
      } as Collection['pieces'][string];
    });

    return {
      ...collection,
      artists: derivedArtists,
      pieces: derivedPieces
    };
  }

  private collectionPieceIds(
    collection: Collection,
    memberships: CollectionPieceMembership[]
  ): string[] {
    if (memberships.length > 0) {
      return memberships.map(membership => membership.pieceId);
    }

    const pieceIds = new Set<string>();

    Object.values(collection.artists || {}).forEach(artistData => {
      const artistPieces = Array.isArray(artistData)
        ? artistData
        : artistData?.pieces;

      artistPieces?.forEach(pieceId => {
        if (pieceId) pieceIds.add(pieceId);
      });
    });

    Object.keys(collection.pieces || {}).forEach(pieceId => {
      if (pieceId) pieceIds.add(pieceId);
    });

    Object.values(collection.series || {}).forEach(series => {
      series.pieces?.forEach(pieceId => {
        if (pieceId) pieceIds.add(pieceId);
      });
    });

    return Array.from(pieceIds);
  }

  /**
   * Get pieces for a specific artist
   */
  getPiecesForArtist(artistPieceMap: Map<string, Piece[]>, artistId: string): Piece[] {
    return artistPieceMap.get(artistId) || [];
  }

  /**
   * Search pieces by title or artist name
   */
  searchPieces(pieces: Piece[], artists: Artist[], query: string): Piece[] {
    if (!query.trim()) return pieces;

    const searchTerm = query.toLowerCase().trim();
    const artistMap = new Map(artists.map(artist => [artist.artistId!, artist]));

    return pieces.filter(piece => {
      // Search by piece title
      if (piece.title.toLowerCase().includes(searchTerm)) {
        return true;
      }

      // Search by artist name
      const artist = artistMap.get(piece.artistID);
      if (artist && artist.displayName.toLowerCase().includes(searchTerm)) {
        return true;
      }

      return false;
    });
  }

  /**
   * Utility: Split array into chunks
   */
  private chunkArray<T>(array: T[], size: number): T[][] {
    const chunks: T[][] = [];
    for (let i = 0; i < array.length; i += size) {
      chunks.push(array.slice(i, i + size));
    }
    return chunks;
  }

  private async mapWithConcurrency<T, R>(
    items: T[],
    concurrency: number,
    mapper: (item: T, index: number) => Promise<R>
  ): Promise<R[]> {
    const results = new Array<R>(items.length);
    let nextIndex = 0;

    const workers = Array.from(
      { length: Math.min(concurrency, items.length) },
      async () => {
        while (nextIndex < items.length) {
          const index = nextIndex;
          nextIndex += 1;
          results[index] = await mapper(items[index], index);
        }
      }
    );

    await Promise.all(workers);
    return results;
  }

  private firstString(...values: unknown[]): string | undefined {
    for (const value of values) {
      if (typeof value === 'string' && value.trim()) {
        return value.trim();
      }
    }

    return undefined;
  }

  private firstNumber(...values: unknown[]): number | undefined {
    for (const value of values) {
      if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
      }

      if (typeof value === 'string' && value.trim() && Number.isFinite(Number(value))) {
        return Number(value);
      }
    }

    return undefined;
  }

  private compareMemberships(
    a?: CollectionPieceMembership,
    b?: CollectionPieceMembership
  ): number {
    if (!a && !b) return 0;
    if (!a) return 1;
    if (!b) return -1;

    if (a.order !== undefined && b.order !== undefined) {
      return a.order - b.order;
    }

    if (a.order !== undefined) return -1;
    if (b.order !== undefined) return 1;

    return a.pieceId.localeCompare(b.pieceId);
  }

  /**
   * Utility: Extract last name from display name
   */
  private getLastName(displayName: string): string {
    const parts = displayName.trim().split(' ');
    return parts[parts.length - 1] || displayName;
  }
}

// Export singleton instance
export const galleryService = new GalleryService();
export default galleryService;
